// The open files' tabs above the editor, as the desktop IDEs have them, after
// the Help tab, which is always first. A tab shows a file; its × closes the
// tab, not the file, which stays in the file list. A middle click closes a
// tab too.

// The Help tab's place in active.
export const HELP = ":help";
// The Modules tab's (src/modules.js), which follows Help while it is open.
export const MODULES = ":modules";

export class FileTabs {
  // element: the tab strip. select(path) shows a tab's file, or the help for
  // HELP; close(path) hears that its tab was closed; changed() that the tabs
  // or the one shown changed, to keep them.
  constructor(element, { select, close, changed = () => {} }) {
    this.element = element;
    this.select = select;
    this.onClose = close;
    this.changed = changed;
    this.paths = [];
    this.active = null;
    this.modules = false;
  }

  // The tabs, as a program kept them. The Modules tab stays as it is.
  reset(paths = [], active = null) {
    this.paths = [...paths];
    this.active = active === MODULES && !this.modules ? null : active;
    this.render();
  }

  // A file is shown: its tab is added, after the one shown, and selected.
  show(path) {
    if (!this.paths.includes(path)) {
      const at = this.active === HELP ? 0 : this.active ? this.paths.indexOf(this.active) + 1 : this.paths.length;
      this.paths.splice(at < 0 ? this.paths.length : at, 0, path);
    }
    this.active = path;
    this.render();
    this.changed();
  }

  // The help is shown.
  showHelp() {
    this.active = HELP;
    this.render();
    this.changed();
  }

  // The Modules tab is shown, opening it if it was closed.
  showModules() {
    this.modules = true;
    this.active = MODULES;
    this.render();
    this.changed();
  }

  // Closes a tab, as its × does. Returns the tab to show next: a file, or
  // HELP when it was the last.
  close(path) {
    if (path === MODULES) {
      this.modules = false;
      if (this.active === MODULES) this.active = this.paths[0] ?? HELP;
      this.render();
      this.changed();
      return this.active;
    }
    const at = this.paths.indexOf(path);
    if (at < 0) return this.active;
    this.paths.splice(at, 1);
    if (this.active === path) this.active = this.paths[Math.min(at, this.paths.length - 1)] ?? HELP;
    this.render();
    this.changed();
    return this.active;
  }

  // A file was deleted: its tab goes, without showing another.
  remove(path) {
    const at = this.paths.indexOf(path);
    if (at < 0) return;
    this.paths.splice(at, 1);
    if (this.active === path) this.active = null;
    this.render();
    this.changed();
  }

  rename(from, to) {
    const at = this.paths.indexOf(from);
    if (at < 0) return;
    this.paths[at] = to;
    if (this.active === from) this.active = to;
    this.render();
    this.changed();
  }

  render() {
    const strip = this.element;
    strip.replaceChildren();
    strip.hidden = false;
    const help = document.createElement("div");
    help.className = "file-tab help-tab";
    help.setAttribute("role", "tab");
    help.setAttribute("aria-selected", String(this.active === HELP));
    help.tabIndex = this.active === HELP ? 0 : -1;
    help.title = "Help (F1 shows the command at the cursor)";
    help.dataset.path = HELP;
    help.textContent = "Help";
    help.addEventListener("click", () => { if (this.active !== HELP) this.select(HELP); });
    help.addEventListener("keydown", (event) => this.arrows(event, HELP));
    strip.append(help);
    if (this.modules) {
      const tab = document.createElement("div");
      tab.className = "file-tab modules-tab";
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", String(this.active === MODULES));
      tab.tabIndex = this.active === MODULES ? 0 : -1;
      tab.title = "Modules: find modules to add to the program, and share your own";
      tab.dataset.path = MODULES;
      const name = document.createElement("span");
      name.className = "name";
      name.textContent = "Modules";
      const close = document.createElement("button");
      close.type = "button";
      close.className = "close";
      close.textContent = "×";
      close.tabIndex = -1;
      close.title = "Close Modules (View > Modules opens it again)";
      close.setAttribute("aria-label", "Close Modules");
      close.addEventListener("click", (event) => {
        event.stopPropagation();
        this.onClose(MODULES);
      });
      tab.append(name, close);
      tab.addEventListener("click", () => { if (this.active !== MODULES) this.select(MODULES); });
      tab.addEventListener("keydown", (event) => this.arrows(event, MODULES));
      strip.append(tab);
    }
    for (const path of this.paths) {
      const tab = document.createElement("div");
      tab.className = "file-tab";
      tab.setAttribute("role", "tab");
      tab.setAttribute("aria-selected", String(path === this.active));
      tab.tabIndex = path === this.active ? 0 : -1;
      tab.title = path;
      tab.dataset.path = path;
      const name = document.createElement("span");
      name.className = "name";
      name.textContent = path.split("/").pop();
      const close = document.createElement("button");
      close.type = "button";
      close.className = "close";
      close.textContent = "×";
      close.tabIndex = -1;
      close.title = `Close ${path}`;
      close.setAttribute("aria-label", `Close ${path}`);
      close.addEventListener("click", (event) => {
        event.stopPropagation();
        this.onClose(path);
      });
      tab.append(name, close);
      tab.addEventListener("click", () => { if (path !== this.active) this.select(path); });
      tab.addEventListener("mousedown", (event) => { if (event.button === 1) event.preventDefault(); });
      tab.addEventListener("auxclick", (event) => {
        if (event.button !== 1) return;
        event.preventDefault();
        this.onClose(path);
      });
      tab.addEventListener("keydown", (event) => {
        if (event.key === "Delete") {
          event.preventDefault();
          this.onClose(path);
        } else {
          this.arrows(event, path);
        }
      });
      strip.append(tab);
    }
    strip.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }

  // The arrow keys move between the tabs, the help's among them.
  arrows(event, path) {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    const all = [HELP, ...(this.modules ? [MODULES] : []), ...this.paths];
    const next = all[(all.indexOf(path) + (event.key === "ArrowRight" ? 1 : -1) + all.length) % all.length];
    event.preventDefault();
    this.select(next);
    this.element.querySelector(`[data-path="${CSS.escape(next)}"]`)?.focus();
  }
}
